<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import WhatsappChannelAPI from 'dashboard/api/channel/whatsappChannel';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  inboxId: { type: [Number, String], required: true },
  // Right after the inbox is created: ask for a QR code at once.
  connectOnMount: { type: Boolean, default: false },
});

const emit = defineEmits(['connected']);

// Evolution rotates the QR code roughly every 30 seconds.
const POLL_INTERVAL_MS = 3000;
const EVOLUTION_URL = 'https://github.com/EvolutionAPI/evolution-api';

const connection = ref(null);
const isLoading = ref(false);
const errorMessage = ref('');
let pollTimer = null;

const state = computed(() => connection.value?.state || 'close');
const isConnected = computed(() => state.value === 'open');
const qrCode = computed(() =>
  state.value === 'connecting' ? connection.value?.qr_code : null
);
const isOtherNumber = computed(() => {
  const digits = number => (number || '').replace(/\D/g, '');
  const { phone_number: phone, expected_phone_number: expected } =
    connection.value || {};
  return Boolean(phone && expected && digits(phone) !== digits(expected));
});

const stopPolling = () => {
  clearInterval(pollTimer);
  pollTimer = null;
};

const apply = response => {
  const wasConnected = isConnected.value;
  connection.value = response.data;
  errorMessage.value = '';
  if (isConnected.value) stopPolling();
  if (isConnected.value && !wasConnected) emit('connected');
};

const handleError = error => {
  errorMessage.value = error.response?.data?.error || error.message;
  stopPolling();
};

const refresh = async () => {
  try {
    apply(await WhatsappChannelAPI.getEvolutionConnection(props.inboxId));
  } catch (error) {
    handleError(error);
  }
};

const startPolling = () => {
  stopPolling();
  pollTimer = setInterval(refresh, POLL_INTERVAL_MS);
};

const connect = async () => {
  isLoading.value = true;
  try {
    apply(await WhatsappChannelAPI.connectEvolution(props.inboxId));
    if (!isConnected.value) startPolling();
  } catch (error) {
    handleError(error);
  } finally {
    isLoading.value = false;
  }
};

const disconnect = async () => {
  isLoading.value = true;
  try {
    apply(await WhatsappChannelAPI.disconnectEvolution(props.inboxId));
  } catch (error) {
    handleError(error);
  } finally {
    isLoading.value = false;
  }
};

onMounted(async () => {
  if (props.connectOnMount) {
    await connect();
    return;
  }
  await refresh();
  if (state.value === 'connecting') startPolling();
});

onBeforeUnmount(stopPolling);
</script>

<template>
  <div class="flex flex-col gap-4">
    <Banner v-if="errorMessage" color="ruby">
      {{ $t('INBOX_MGMT.WHATSAPP_EVOLUTION.ERROR', { error: errorMessage }) }}
    </Banner>

    <div v-if="!connection && !errorMessage" class="flex justify-center py-8">
      <Spinner />
    </div>

    <div
      v-else-if="isConnected"
      class="flex flex-col gap-3 p-4 rounded-xl border border-n-weak"
    >
      <p class="flex items-center gap-2 text-sm font-medium text-n-slate-12">
        <span class="size-2 rounded-full bg-n-teal-9" />
        {{
          $t('INBOX_MGMT.WHATSAPP_EVOLUTION.CONNECTED', {
            phoneNumber: connection.phone_number,
          })
        }}
      </p>
      <Banner v-if="isOtherNumber" color="amber">
        {{
          $t('INBOX_MGMT.WHATSAPP_EVOLUTION.OTHER_NUMBER', {
            expected: connection.expected_phone_number,
          })
        }}
      </Banner>
      <div>
        <Button
          :label="$t('INBOX_MGMT.WHATSAPP_EVOLUTION.DISCONNECT')"
          variant="faded"
          color="ruby"
          size="sm"
          :is-loading="isLoading"
          @click="disconnect"
        />
      </div>
    </div>

    <div
      v-else-if="qrCode"
      class="flex flex-col items-center gap-4 p-4 rounded-xl border border-n-weak md:flex-row md:items-start"
    >
      <img
        :src="qrCode"
        :alt="$t('INBOX_MGMT.WHATSAPP_EVOLUTION.QR_ALT')"
        class="size-64 rounded-lg bg-white p-2"
      />
      <div class="flex flex-col gap-2 text-sm text-n-slate-11">
        <p class="font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.WHATSAPP_EVOLUTION.SCAN_TITLE') }}
        </p>
        <ol class="flex flex-col gap-1 ps-4 list-decimal">
          <li>{{ $t('INBOX_MGMT.WHATSAPP_EVOLUTION.STEP_1') }}</li>
          <li>{{ $t('INBOX_MGMT.WHATSAPP_EVOLUTION.STEP_2') }}</li>
          <li>{{ $t('INBOX_MGMT.WHATSAPP_EVOLUTION.STEP_3') }}</li>
        </ol>
        <p>{{ $t('INBOX_MGMT.WHATSAPP_EVOLUTION.REFRESH_NOTE') }}</p>
      </div>
    </div>

    <div v-else class="flex flex-col gap-3 p-4 rounded-xl border border-n-weak">
      <p class="flex items-center gap-2 text-sm font-medium text-n-slate-12">
        <span class="size-2 rounded-full bg-n-ruby-9" />
        {{ $t('INBOX_MGMT.WHATSAPP_EVOLUTION.DISCONNECTED') }}
      </p>
      <div>
        <Button
          :label="$t('INBOX_MGMT.WHATSAPP_EVOLUTION.GENERATE_QR')"
          size="sm"
          :is-loading="isLoading"
          @click="connect"
        />
      </div>
    </div>

    <p class="text-xs text-n-slate-11">
      {{ $t('INBOX_MGMT.WHATSAPP_EVOLUTION.RISK_NOTE') }}
    </p>
    <p class="text-xs text-n-slate-11">
      {{ $t('INBOX_MGMT.WHATSAPP_EVOLUTION.EVOLUTION_NOTICE') }}
      <a
        :href="EVOLUTION_URL"
        target="_blank"
        rel="noopener noreferrer"
        class="underline"
      >
        {{ $t('INBOX_MGMT.WHATSAPP_EVOLUTION.EVOLUTION_LINK') }}
      </a>
    </p>
  </div>
</template>
